import type { AccidentPhotoRow, AccidentReportRow, TruckRow } from '@routeback/core';
import { useLiveQuery } from 'dexie-react-hooks';
import type { ReactNode } from 'react';
import { PhotoThumb } from '../../components/PhotoThumb';
import { useApp } from '../../lib/app';
import type { AccidentData } from './accident';

/** Label: value lines, skipping blanks. */
function Lines({ rows }: { rows: [string, string | undefined][] }) {
  const shown = rows.filter(([, v]) => v && v.trim());
  if (!shown.length) return <p className="muted small" style={{ margin: 0 }}>Nothing entered.</p>;
  return <dl className="ar-dl">{shown.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>;
}

/** The whole report, read-only — used for review (with Edit links) and after sending. */
export function ReportSummary({ report, data: d, photos, onEdit }: { report: AccidentReportRow; data: AccidentData; photos: AccidentPhotoRow[]; onEdit?: (step: number) => void }) {
  const app = useApp();
  const truck = useLiveQuery(() => (report.truck_id ? app.db.table<TruckRow, string>('trucks').get(report.truck_id) : undefined), [app.db, report.truck_id]);
  const Section = ({ title, step, children }: { title: string; step: number; children: ReactNode }) => (
    <section className="ar-sum">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong>{title}</strong>
        {onEdit && <button type="button" className="btn btn-sm btn-ghost" onClick={() => onEdit(step)}>Edit</button>}
      </div>
      {children}
    </section>
  );
  const when = d.date ? new Date(`${d.date}T${d.time || '12:00'}`).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', ...(d.time ? { hour: 'numeric', minute: '2-digit' } : {}) }) : '';
  const byCat = (c: AccidentPhotoRow['category']) => photos.filter((p) => p.category === c);
  return (
    <div>
      <Section title="Driver & vehicle" step={0}>
        <Lines rows={[['Driver', app.userId === report.user_id ? app.userName : undefined], ['FedEx ID', d.fedex_id], ['When', when],
          ['Unit #', truck ? truck.unit_number : d.unit_other], ['Vehicle type', d.vehicle_type], ['Ownership', d.ownership],
          ['VEDR camera in vehicle', d.vedr], ['VEDR camera is with', d.vedr === 'No' ? d.vedr_possession : '']]} />
      </Section>
      <Section title="Location" step={1}>
        <Lines rows={[['Street / highway', d.loc.street], ['Block / mile marker', d.loc.block], ['City', d.loc.city], ['County', d.loc.county], ['State', d.loc.state],
          ['Nearest landmark', d.loc.landmark], ['Property type', d.loc.property_type]]} />
      </Section>
      <Section title={`Other vehicles (${d.others.length})`} step={2}>
        {d.others.length === 0 ? <p className="muted small" style={{ margin: 0 }}>None.</p> : d.others.map((v, i) => (
          <div key={i} style={{ marginTop: i ? 10 : 0 }}>
            <div className="small" style={{ fontWeight: 600 }}>Vehicle {i + 1}</div>
            <Lines rows={[['Vehicle', v.vehicle], ['Plate', [v.plate, v.plate_state].filter(Boolean).join(' ')], ['Insurance', [v.insurance, v.policy && `policy ${v.policy}`].filter(Boolean).join(', ')],
              ['Owner', [v.owner, v.address, v.phone].filter(Boolean).join(' · ')], ['Driver', [v.driver_name, v.driver_phone, v.driver_address].filter(Boolean).join(' · ')],
              ['Driver DOB', v.driver_dob], ['Driver’s license', [v.driver_license, v.driver_license_state].filter(Boolean).join(' ')]]} />
          </div>
        ))}
      </Section>
      <Section title="Witnesses & police" step={3}>
        <Lines rows={[...d.witnesses.map((w, i) => [`Witness ${i + 1}`, [w.name, w.phone, w.address, w.age && `about ${w.age}`].filter(Boolean).join(' · ')] as [string, string]),
          ['Police called', d.police.called], ['Jurisdiction', d.police.jurisdiction], ['Officer', [d.police.officer, d.police.badge && `badge ${d.police.badge}`].filter(Boolean).join(', ')],
          ['Post / district', d.police.post], ['Cited', d.police.cited], ['Violation', d.police.violation]]} />
      </Section>
      <Section title="Road & weather" step={4}>
        <Lines rows={[['Speed limit', d.road.speed_limit && `${d.road.speed_limit} mph`], ['Lanes', d.road.lanes], ['Road', d.road.characteristics.join(', ')],
          ['Surface', d.road.surface.join(', ')], ['Weather', d.road.weather.join(', ')], ['Visibility', d.road.visibility],
          ['Property damaged', d.property.damage], ['Property owner', [d.property.owner, d.property.phone].filter(Boolean).join(' · ')]]} />
      </Section>
      <Section title="What happened" step={5}>
        <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0' }}>{d.description || <span className="muted">Not written yet.</span>}</p>
        <Lines rows={[['Injuries', d.injuries], ['Other notes', d.notes]]} />
      </Section>
      <Section title={`Photos (${photos.length})`} step={6}>
        {([['my_vehicle', 'Your vehicle'], ['other_vehicle', 'Other vehicle'], ['scene', 'Scene'], ['other', 'Other']] as const).map(([c, label]) => byCat(c).length ? (
          <div key={c} style={{ marginTop: 6 }}>
            <div className="muted small">{label}</div>
            <div className="row row-wrap" style={{ gap: 6, marginTop: 4 }}>{byCat(c).map((p) => <PhotoThumb key={p.id} path={p.file_path} size={64} />)}</div>
          </div>
        ) : null)}
        {!photos.length && <p className="muted small" style={{ margin: 0 }}>No photos.</p>}
      </Section>
    </div>
  );
}
